import React, { useState, MouseEvent, ReactElement } from 'react';
import ProfileIcon from '@material-ui/icons/AccountCircleOutlined';
import LogoutIcon from '@material-ui/icons/ExitToApp';
import MenuItem from 'components/menu-item';
//
import {
  AddDealContainer,
  HeaderButtonGroup,
  HeaderButtons,
  HeaderIconContainer,
  LogoContainer,
  LogoutMenuItem,
  MenuItemText,
  Tab,
  TabContainer,
  Username,
} from 'components/header/components';
import history from 'lib/history';
import routes from 'router/routes';
import { isAuthorized, getUserName } from 'lib/auth';
import StyledHeader from 'components/header/styled-header';
import Menu from 'components/menu';
import ButtonPrimary from 'components/buttons/primary';
import { logoutTestid } from 'components/sidebar/base-navigation/test-ids';
import AcronymCircleButton from 'components/buttons/acronym-circle';
import labels from 'constants/labels';
import ModalBasic from 'components/modal-provider/modal-basic';
import { ModalButton, ModalDontSaveButton, ModalTextButton } from 'components/modal-provider/modal';
import messages from 'constants/messages';
import colors from 'theme/colors';
import { EModalTitle } from 'lib/storage';
import BookmarkViewIcon from 'assets/svg/BookmarkViewIcon';
import HelpIcon from 'assets/svg/HelpIcon';
import FeedbackIcon from 'assets/svg/FeedbackIcon';
import PricingToolkitIcon from 'assets/svg/PricingToolkitIcon';
import WhatsNewIcon from 'assets/svg/WhatsNewIcon';
import SalesForceIcon from 'assets/svg/SalesForceIcon';
import IdeasTrackerIcon from 'assets/svg/IdeasTrackerIcon';

const { onyx, yellow } = colors;

interface IHeaderProps {
  pageTitle: string;
  visible: boolean;
  resetSidebar: () => void;
  editDealCancel: () => void;
  clearDealEvaluation: () => void;
  cancelFormulaEdit: () => void;
  isDealDirty: boolean;
  clearModalContent: () => void;
  saveDeal: (callback?: Function) => void;
  setModalContent: (content: ReactElement) => void;
  setParetoIsModalOpen: (isModalOpen: boolean, title: EModalTitle) => void;
  paretoDealIds: string[];
  portfolioDealIds: string[];
  compareDealIds: string[];
  displayNavHeader: boolean;
}

enum ESiteTab {
  DASHBOARD = 'dashboard',
  COMPARE = 'compare',
  PARETO = 'pareto',
  PORTFOLIO = 'portfolio',
  WHATSNEW = 'whatsnew',
}

const Header = ({
  visible,
  resetSidebar,
  editDealCancel,
  clearDealEvaluation,
  cancelFormulaEdit,
  isDealDirty,
  clearModalContent,
  setModalContent,
  setParetoIsModalOpen,
  saveDeal,
  paretoDealIds,
  portfolioDealIds,
  compareDealIds,
  displayNavHeader,
}: IHeaderProps) => {
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);

  const handleOtherTabClick = (route: string, isSaveDeal: boolean, dealIds?: string[]) => {
    clearModalContent();
    if (isDealDirty && isSaveDeal) {
      saveDeal();
    }
    resetSidebar();
    editDealCancel();
    clearDealEvaluation();
    cancelFormulaEdit();
    if (dealIds && dealIds.length > 0) {
      history.push(`${route}/${dealIds}`);
    } else {
      history.push(route);
    }
  };

  function openModal(route: string) {
    const buttons = (
      <>
        <ModalTextButton onClick={clearModalContent}>{labels.button.cancel}</ModalTextButton>
        <ModalDontSaveButton onClick={() => handleOtherTabClick(route, false)}>
          {labels.button.dontsave}
        </ModalDontSaveButton>
        <ModalButton onClick={() => handleOtherTabClick(route, true)}>{labels.button.save}</ModalButton>
      </>
    );

    const modalContent = <p>{messages.warnings.unsavedChanges}</p>;

    const modal = (
      <ModalBasic
        title={labels.titles.modals.save}
        content={modalContent}
        onClose={clearModalContent}
        buttons={buttons}
      />
    );

    setModalContent(modal);
  }

  function requestBackToDashboard() {
    // show modal
    if (isDealDirty) {
      openModal(routes.dashboard);
    } else {
      handleOtherTabClick(routes.dashboard, true);
    }
  }

  function requestCompareTab() {
    // show modal
    if (isDealDirty) {
      openModal(routes.compare);
    } else {
      handleOtherTabClick(routes.compare, true, compareDealIds);
    }
  }

  function requestParetoTab() {
    // show modal
    if (isDealDirty) {
      openModal(routes.pareto);
    } else {
      setParetoIsModalOpen(false, EModalTitle.PARETO);
      handleOtherTabClick(routes.pareto, true, paretoDealIds);
    }
  }

  function requestPortfolioTab() {
    // show modal
    if (isDealDirty) {
      openModal(routes.portfolio);
    } else {
      setParetoIsModalOpen(false, EModalTitle.PORTFOLIO);
      handleOtherTabClick(routes.portfolio, true, portfolioDealIds);
    }
  }

  function handleFeedback() {
    window.open(routes.feedback, routes.feedback);
  }

  function handleHelp() {
    window.open(routes.help, routes.help);
  }

  function handleReportAnIssue() {
    window.open(routes.reportAnIssue, routes.reportAnIssue);
  }

  function handleLogout() {
    window.location.replace(window.location.origin);
  }

  function toggleProfileMenu(event: MouseEvent<HTMLElement>) {
    const anchorValue = profileMenuAnchor ? null : event.currentTarget;
    setProfileMenuAnchor(anchorValue);
  }

  function loadPricingToolKit() {
    window.open(routes.pricingExcellence, routes.pricingExcellence);
  }

  function handlesalesforce() {
    window.open(routes.salesforce, routes.salesforce);
  }

  function renderButtons() {
    if (!isAuthorized()) {
      return null;
    }

    return (
      <>
        {!visible && displayNavHeader && (
          <AddDealContainer>
            <ButtonPrimary
              onClick={() => {
                history.push(routes.dealCreate);
              }}
              text="Add Deal"
              backgroundColor={yellow}
              color={onyx}
              width={120}
              fontSize="14px"
            />
          </AddDealContainer>
        )}
        {displayNavHeader && (
          <HeaderButtonGroup>
            <HeaderIconContainer onClick={toggleProfileMenu}>
              <Username>{getUserName()}</Username>
              <ProfileIcon />
              <Menu
                id="header-menu"
                anchorEl={profileMenuAnchor}
                keepMounted
                open={Boolean(profileMenuAnchor)}
                onClose={toggleProfileMenu}
              >
                <MenuItem onClick={() => history.push(routes.bookmark)}>
                  <BookmarkViewIcon />
                  <MenuItemText>{labels.header.bookmark}</MenuItemText>
                </MenuItem>
                <MenuItem onClick={() => history.push(routes.whatsnew)}>
                  <WhatsNewIcon />
                  <MenuItemText>{labels.header.whatsNew}</MenuItemText>
                </MenuItem>
                <MenuItem onClick={handleReportAnIssue}>
                  <IdeasTrackerIcon />
                  <MenuItemText>{labels.header.reportAnIssue}</MenuItemText>
                </MenuItem>
                <MenuItem onClick={handleFeedback}>
                  <FeedbackIcon />
                  <MenuItemText>{labels.header.feedback}</MenuItemText>
                </MenuItem>
                <MenuItem onClick={handleHelp}>
                  <HelpIcon />
                  <MenuItemText>{labels.header.help}</MenuItemText>
                </MenuItem>
                <MenuItem onClick={handlesalesforce}>
                  <SalesForceIcon />
                  <MenuItemText>{labels.header.salesforce}</MenuItemText>
                </MenuItem>
                <MenuItem onClick={loadPricingToolKit}>
                  <PricingToolkitIcon />
                  <MenuItemText>{labels.header.pricingToolKit}</MenuItemText>
                </MenuItem>
                <LogoutMenuItem onClick={handleLogout} data-testid={logoutTestid}>
                  <LogoutIcon />
                  <MenuItemText>Logout</MenuItemText>
                </LogoutMenuItem>
              </Menu>
            </HeaderIconContainer>
          </HeaderButtonGroup>
        )}
      </>
    );
  }

  // Determine active tab
  let activeTab = ESiteTab.DASHBOARD;
  const baseRoute = history.location.pathname.split('/');
  if (baseRoute.length > 1 && baseRoute[1] === ESiteTab.COMPARE) {
    activeTab = ESiteTab.COMPARE;
  }
  if (baseRoute.length > 1 && baseRoute[1] === ESiteTab.PARETO) {
    activeTab = ESiteTab.PARETO;
  }
  if (baseRoute.length > 1 && baseRoute[1] === ESiteTab.PORTFOLIO) {
    activeTab = ESiteTab.PORTFOLIO;
  }
  if (baseRoute.length > 1 && baseRoute[1] === ESiteTab.WHATSNEW) {
    activeTab = ESiteTab.WHATSNEW;
  }
  return (
    <StyledHeader>
      <LogoContainer>
        <AcronymCircleButton title="DA" onClick={() => null} disableHoverEffects />
      </LogoContainer>
      {isAuthorized() && displayNavHeader && (
        <TabContainer>
          <Tab
            data-testid="tid-dashboard-button"
            isActive={activeTab === ESiteTab.DASHBOARD}
            onClick={requestBackToDashboard}
          >
            Dashboard
          </Tab>
          <Tab
            data-testid="tid-deal-comparison-button"
            isActive={activeTab === ESiteTab.COMPARE}
            onClick={requestCompareTab}
          >
            Deal Comparison
          </Tab>
          <Tab data-testid="tid-pareto-button" isActive={activeTab === ESiteTab.PARETO} onClick={requestParetoTab}>
            Pareto
          </Tab>
          <Tab
            data-testid="tid-portfolio-button"
            isActive={activeTab === ESiteTab.PORTFOLIO}
            onClick={requestPortfolioTab}
          >
            Portfolio
          </Tab>
        </TabContainer>
      )}
      <HeaderButtons>{renderButtons()}</HeaderButtons>
    </StyledHeader>
  );
};

export default Header;
